import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMystudioMyPageV4 } from "../../Common/api/v4";
import { MobileBottomTabBar } from "../MobileAppShell";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import MystudioDataState from "../MystudioDataState";
import "./MobileMyPageDocuments.css";

const PAGE_META = {
  contract: {
    title: "계약서",
    eyebrow: "계약 문서",
    description: "스튜디오 계약 내용을 문서 형태로 확인할 수 있어요.",
  },
  payments: {
    title: "결제내역",
    eyebrow: "결제 요약",
    description: "계약별 결제 완료 금액과 남은 금액을 한눈에 확인해요.",
  },
  receipts: {
    title: "영수증",
    eyebrow: "영수증 보관함",
    description: "결제 건별 영수증을 확인하고 저장할 수 있어요.",
  },
};

function formatMoney(value) {
  const numberValue = Number(String(value || "").replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(numberValue)) return "0";
  return numberValue.toLocaleString("ko-KR");
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).replace(/-/g, ".");
  const week = ["일", "월", "화", "수", "목", "금", "토"][date.getDay()];
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}(${week})`;
}

function normalizeImageUrl(image) {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.middle_url || image.middle_img || image.url || image.small_url || "";
}

function StatusBar() {
  return (
    <div className="mypage-doc-status" aria-hidden="true">
      <span>9:41</span>
      <span className="mypage-doc-status__icons">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function Header({ title, onBack }) {
  return (
    <header className="mypage-doc-header">
      <button type="button" onClick={onBack} aria-label="뒤로가기">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 5-7 7 7 7" />
        </svg>
      </button>
      <h1>{title}</h1>
      <span aria-hidden="true" />
    </header>
  );
}

function MoneySummary({ contract }) {
  const hasAmountData = [
    contract.contract_totamount,
    contract.contract_payamount,
    contract.contract_balance,
  ].some((value) => value !== undefined && value !== null && value !== "");
  if (!hasAmountData) {
    return <p className="mypage-doc-state">계약 금액 상세 정보는 아직 제공되지 않습니다.</p>;
  }
  return (
    <section className="mypage-doc-summary-card">
      <div>
        <span>총 계약금액</span>
        <strong>{formatMoney(contract.contract_totamount)}원</strong>
      </div>
      <div>
        <span>결제 완료</span>
        <strong>{formatMoney(contract.contract_payamount)}원</strong>
      </div>
      <div>
        <span>남은 금액</span>
        <strong>{formatMoney(contract.contract_balance)}원</strong>
      </div>
    </section>
  );
}

function Hero({ mode, contract }) {
  const meta = PAGE_META[mode];

  return (
    <section className={`mypage-doc-hero is-${mode}`}>
      <span>{meta.eyebrow}</span>
      <h2>{contract.package_name || "계약 상품"}</h2>
      <p>{meta.description}</p>
      <small>계약번호 {contract.contract_id}</small>
    </section>
  );
}

function ContractPreview({ contract }) {
  const images = (contract.img_list || []).map(normalizeImageUrl).filter(Boolean);

  return (
    <section className="mypage-doc-panel">
      <div className="mypage-doc-panel__head">
        <h3>계약서 미리보기</h3>
        <span>{formatDate(contract.contract_date)}</span>
      </div>
      {images.length > 0 ? (
        <div className="mypage-doc-image-list">
          {images.map((image, index) => (
            <a key={`${image}-${index}`} className="mypage-doc-image-card" href={image} target="_blank" rel="noreferrer">
              <img src={image} alt={`계약서 ${index + 1}페이지`} />
              <span>{index + 1}페이지 보기</span>
            </a>
          ))}
        </div>
      ) : (
        <p className="mypage-doc-state">계약서 파일이 등록되면 이곳에서 확인할 수 있습니다.</p>
      )}
    </section>
  );
}

function PaymentList({ contracts }) {
  return (
    <section className="mypage-doc-list">
      {contracts.map((contract) => (
        <article className="mypage-doc-contract-card" key={contract.contract_id}>
          <div className="mypage-doc-contract-card__top">
            <strong>{contract.package_name}</strong>
            <span>결제완료</span>
          </div>
          <MoneySummary contract={contract} />
          <div className="mypage-doc-payment-items">
            {(contract.pay_list || []).map((pay, index) => (
              <div className="mypage-doc-payment-row" key={pay.id || `${contract.contract_id}-${index}`}>
                <span>
                  <b>{pay.pay_type_name || "결제"}</b>
                  <small>{formatDate(pay.pay_date)} · {pay.detail_type_name || "결제수단"}</small>
                </span>
                <strong>{formatMoney(pay.pay_amount)}원</strong>
              </div>
            ))}
          </div>
          {(contract.pay_list || []).length === 0 ? <p className="mypage-doc-state">결제 상세 내역이 아직 제공되지 않습니다.</p> : null}
        </article>
      ))}
    </section>
  );
}

function ReceiptList({ contracts }) {
  const receipts = contracts.flatMap((contract) =>
    (contract.pay_list || []).map((pay, index) => ({ contract, pay, index }))
  );

  if (receipts.length === 0) {
    return <p className="mypage-doc-state">표시할 영수증이 없습니다.</p>;
  }
  return (
    <section className="mypage-doc-list">
      {receipts.map(({ contract, pay, index }) => (
        <article className="mypage-doc-receipt-card" key={`${contract.contract_id}-${pay.id || index}`}>
          <div className="mypage-doc-receipt-card__stamp">PAID</div>
          <div className="mypage-doc-receipt-card__body">
            <span>{contract.package_name}</span>
            <strong>{formatMoney(pay.pay_amount)}원</strong>
            <dl>
              <div><dt>결제일</dt><dd>{formatDate(pay.pay_date)}</dd></div>
              <div><dt>결제수단</dt><dd>{pay.detail_type_name || "카드 결제"}</dd></div>
              <div><dt>승인번호</dt><dd>{pay.approval_number || "ERP 미등록"}</dd></div>
              {pay.proof_type ? <div><dt>증빙 종류</dt><dd>{pay.proof_type}</dd></div> : null}
            </dl>
          </div>
        </article>
      ))}
    </section>
  );
}

export default function MobileMyPageDocuments({ mode = "payments" }) {
  const navigate = useNavigate();
  const meta = PAGE_META[mode] || PAGE_META.payments;
  const myPageQuery = useMystudioMyPageV4();
  const contracts = useMemo(
    () => {
      const profile = myPageQuery.data?.data;
      const payments = profile?.payments || [];
      return (profile?.contracts || []).map((contract) => ({
        contract_id: contract.id,
        contract_date: contract.contractDate || "",
        package_name: contract.title,
        contract_totamount: contract.totalAmount,
        contract_payamount: contract.paidAmount,
        contract_balance: contract.remainingAmount,
        img_list: (contract.documents || []).map((document) => ({
          url: document.url,
          file_name: document.fileName,
        })),
        pay_list: payments
          .filter((payment) => String(payment.contractId) === String(contract.id))
          .map((payment) => ({
            id: payment.id,
            pay_type_name: payment.type || (
              payment.source === "advance"
                ? "선금"
                : payment.source === "interim"
                  ? "중도금"
                  : "계약 금액"
            ),
            detail_type_name: payment.status === "PAID" ? "결제 완료" : "등록",
            pay_date: payment.date,
            pay_amount: payment.amount,
            approval_number: payment.approvalNumber,
            proof_type: payment.proofType,
          })),
      }));
    },
    [myPageQuery.data],
  );
  const primaryContract = contracts[0] || null;

  const handleBack = () => navigate(withMobileView("/tabs/mypage"));
  const handleTabNavigate = (target) => navigate(withMobileView(target));

  return (
    <section className="mypage-doc-page mobile-app-frame">
      <StatusBar />
      <Header title={meta.title} onBack={handleBack} />
      <main className="mypage-doc-scroll">
        {!myPageQuery.configured ? (
          <MystudioDataState type="error" title="계약 정보를 불러올 수 없어요" description="MyStudio API 연결 설정을 확인해 주세요." />
        ) : myPageQuery.isLoading ? (
          <MystudioDataState type="loading" title="계약 정보를 불러오고 있어요" />
        ) : myPageQuery.isError ? (
          <MystudioDataState type="error" error={myPageQuery.error} onRetry={() => myPageQuery.refetch()} />
        ) : contracts.length === 0 ? (
          <MystudioDataState type="empty" title="등록된 계약이 없어요" description="계약 정보가 등록되면 이곳에서 확인할 수 있어요." />
        ) : (
          <>
            <Hero mode={mode} contract={primaryContract} />
            {mode === "contract" ? (
          <>
            <MoneySummary contract={primaryContract} />
            <ContractPreview contract={primaryContract} />
          </>
            ) : null}
            {mode === "payments" ? <PaymentList contracts={contracts} /> : null}
            {mode === "receipts" ? <ReceiptList contracts={contracts} /> : null}
          </>
        )}
      </main>
      <MobileBottomTabBar activePath="/tabs/mypage" onNavigate={handleTabNavigate} />
    </section>
  );
}
